import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Calendar, Clock, DollarSign, Key, FileText } from 'lucide-react';
import { useCreateScheduledPix, CreateScheduledPixDto, getKeyTypeLabel } from '../../hooks/usePixScheduler';
import { useAccounts } from '../../hooks/useAccounts';

const keyTypes = ['CPF', 'CNPJ', 'EMAIL', 'PHONE', 'EVP'] as const;

export function ScheduledPixCreate() {
  const navigate = useNavigate();
  const { data: accounts, isLoading: loadingAccounts } = useAccounts();
  const createMutation = useCreateScheduledPix();
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CreateScheduledPixDto>({
    defaultValues: {
      scheduledTime: '09:00',
    },
  });

  const amount = watch('amount');

  const onSubmit = async (data: CreateScheduledPixDto) => {
    try {
      setError(null);
      await createMutation.mutateAsync({
        ...data,
        amount: Math.round(Number(data.amount) * 100), // Converter para centavos
      });
      navigate('/banking/pix/scheduled');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao criar agendamento');
    }
  };

  // Calcular data mínima (amanhã) e máxima (365 dias)
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 365);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Voltar
      </button>

      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Agendar PIX</h1>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Conta de Origem */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Conta de Origem
            </label>
            <select
              {...register('accountId', { required: 'Selecione uma conta' })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={loadingAccounts}
            >
              <option value="">Selecione uma conta</option>
              {accounts?.map((account: any) => (
                <option key={account.id} value={account.id}>
                  {account.name} - Saldo: R$ {(account.balance / 100).toFixed(2)}
                </option>
              ))}
            </select>
            {errors.accountId && (
              <p className="mt-1 text-sm text-red-600">{errors.accountId.message}</p>
            )}
          </div>

          {/* Tipo de Chave */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Key className="w-4 h-4 inline mr-1" />
              Tipo de Chave PIX
            </label>
            <select
              {...register('destinationKeyType', { required: 'Selecione o tipo de chave' })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Selecione o tipo</option>
              {keyTypes.map((type) => (
                <option key={type} value={type}>
                  {getKeyTypeLabel(type)}
                </option>
              ))}
            </select>
            {errors.destinationKeyType && (
              <p className="mt-1 text-sm text-red-600">{errors.destinationKeyType.message}</p>
            )}
          </div>

          {/* Chave PIX */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Chave PIX de Destino
            </label>
            <input
              type="text"
              {...register('destinationKey', { required: 'Informe a chave PIX' })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Digite a chave PIX"
            />
            {errors.destinationKey && (
              <p className="mt-1 text-sm text-red-600">{errors.destinationKey.message}</p>
            )}
          </div>

          {/* Valor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <DollarSign className="w-4 h-4 inline mr-1" />
              Valor (R$)
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              {...register('amount', {
                required: 'Informe o valor',
                min: { value: 0.01, message: 'Valor mínimo é R$ 0,01' },
              })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="0,00"
            />
            {errors.amount && (
              <p className="mt-1 text-sm text-red-600">{errors.amount.message}</p>
            )}
            {amount && (
              <p className="mt-1 text-sm text-gray-500">
                Valor em centavos: {Math.round(Number(amount) * 100)}
              </p>
            )}
          </div>

          {/* Data Agendada */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Data do Agendamento
            </label>
            <input
              type="date"
              {...register('scheduledDate', { required: 'Selecione a data' })}
              min={minDate}
              max={maxDateStr}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {errors.scheduledDate && (
              <p className="mt-1 text-sm text-red-600">{errors.scheduledDate.message}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              Máximo de 365 dias a partir de hoje
            </p>
          </div>

          {/* Horário */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Clock className="w-4 h-4 inline mr-1" />
              Horário (opcional)
            </label>
            <input
              type="time"
              {...register('scheduledTime')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="w-4 h-4 inline mr-1" />
              Descrição (opcional)
            </label>
            <input
              type="text"
              {...register('description')}
              maxLength={140}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Descrição da transferência"
            />
          </div>

          {/* Botões */}
          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting || createMutation.isPending}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting || createMutation.isPending ? 'Agendando...' : 'Agendar PIX'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ScheduledPixCreate;
